import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { customerPostLogoutPath } from '@/lib/customerShopReturn'
import { useShopStorefrontLocale } from '@/contexts/ShopStorefrontLocaleContext'

type Props = {
  shopSlug: string
  primaryColor: string
}

/**
 * Storefront account control: avatar opens My account + Sign out (no extra portal hop).
 */
export function ShopStorefrontAccountMenu({ shopSlug, primaryColor }: Props) {
  const { t } = useShopStorefrontLocale()
  const { user, performLogout } = useAuthStore()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const initial = useMemo(() => {
    const name = typeof user?.name === 'string' ? user.name.trim() : ''
    if (name) return name.charAt(0).toUpperCase()
    const email = typeof user?.email === 'string' ? user.email.trim() : ''
    return email ? email.charAt(0).toUpperCase() : '?'
  }, [user?.name, user?.email])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleSignOut = () => {
    setOpen(false)
    const dest = shopSlug.trim()
      ? `/shop/${encodeURIComponent(shopSlug.trim())}`
      : customerPostLogoutPath()
    void performLogout().finally(() => {
      window.location.assign(dest)
    })
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold shadow-sm transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          color: primaryColor,
          borderColor: `color-mix(in srgb, ${primaryColor} 35%, white)`,
          backgroundColor: `color-mix(in srgb, ${primaryColor} 12%, white)`,
          outlineColor: primaryColor,
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('header.openAccountMenu')}
      >
        {initial}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <p className="truncate text-sm font-medium text-slate-900">
              {user?.name || t('header.account')}
            </p>
            {user?.email ? (
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            ) : null}
          </div>
          <Link
            role="menuitem"
            to="/customer"
            className="block px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => setOpen(false)}
          >
            {t('header.myAccount')}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="block w-full px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            {t('header.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
